import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, Button, FormItem } from 'vnetwork-ui'
import { useForm } from 'react-hook-form'
import {
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    FormLabel,
} from '@mui/material'
import dayjs from 'dayjs'
import toast from 'react-hot-toast'

import { ResendType } from '../../types/payload'

interface ResendReportDialogProps {
    open: boolean
    reportId: number
    onConfirm: (payload: ResendType) => void
    onClose: () => void
    isLoading?: boolean
}

interface FormValues {
    mode: 'resend_last' | 'custom'
    date_range?: [string, string]
}

const ResendReportDialog: React.FC<ResendReportDialogProps> = ({
    open,
    reportId,
    onConfirm,
    onClose,
    isLoading = false,
}) => {
    const { t } = useTranslation('report_waf')
    const [mode, setMode] = useState<'resend_last' | 'custom'>('resend_last')

    const { control, handleSubmit } = useForm<FormValues>({
        defaultValues: {
            mode: 'resend_last',
            date_range: undefined,
        },
    })

    // ELK (v1 engine) only keeps raw security logs for 45 days, so a
    // custom-range resend outside that window would regenerate against
    // an empty dataset. Enforce the ceiling at the dialog level:
    //   gte ≥ today − 45 days
    //   lte ≤ today (end-of-day inclusive)
    // The upper bound matches the server-side window handling which
    // treats lte as 23:59:59 of the picked day.
    const ELK_RETENTION_DAYS = 45
    const minGte = dayjs().startOf('day').subtract(ELK_RETENTION_DAYS, 'day')
    const maxLte = dayjs().endOf('day')

    const onSubmit = (data: FormValues) => {
        // Backend contract: `mode: resend_last` → backend resolves
        // gte/lte/type from the latest completed task; for `custom` we
        // send explicit gte/lte + type='customize'. reportId is a number
        // on the calling hook but ResendType.id is a string (controller
        // reads from params[:id] regardless), so coerce.
        const payload: ResendType = {
            id: String(reportId),
            mode,
            type: 'customize',
        }

        if (
            mode === 'custom' &&
            data.date_range &&
            data.date_range.length === 2
        ) {
            const [start, end] = data.date_range
            const startDate = dayjs(start)
            const endDate = dayjs(end)

            // Validate dates
            if (!startDate.isValid() || !endDate.isValid()) {
                toast.error(t('errors.invalid_date_range'))
                return
            }

            // Check start < end
            if (startDate.isAfter(endDate)) {
                toast.error(t('errors.start_after_end'))
                return
            }

            // ELK retention check — hard boundary, not a warning.
            if (startDate.isBefore(minGte, 'day')) {
                toast.error(
                    t('errors.gte_before_retention', {
                        days: ELK_RETENTION_DAYS,
                        defaultValue: `Start date must be within the last ${ELK_RETENTION_DAYS} days (ELK log retention).`,
                    })
                )
                return
            }
            if (endDate.isAfter(maxLte, 'day')) {
                toast.error(
                    t('errors.lte_in_future', {
                        defaultValue: 'End date cannot be in the future.',
                    })
                )
                return
            }

            payload.gte = startDate.format('YYYY-MM-DDT00:00:00[Z]')
            payload.lte = endDate.format('YYYY-MM-DDT23:59:59[Z]')
        } else if (mode === 'custom') {
            // Custom mode but no date range selected
            toast.error(t('errors.date_required'))
            return
        }

        onConfirm(payload)
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={t('resend_dialog.title')}
            maxWidth="sm"
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="p-4 space-y-4">
                    <div className="text-sm text-gray-600 mb-4">
                        {t('resend_dialog.description')}
                    </div>

                    <FormControl component="fieldset" fullWidth>
                        <FormLabel component="legend" className="mb-2">
                            {t('resend_dialog.mode_label')}
                        </FormLabel>
                        <RadioGroup
                            value={mode}
                            onChange={(e) =>
                                setMode(
                                    e.target.value as 'resend_last' | 'custom'
                                )
                            }
                        >
                            <FormControlLabel
                                value="resend_last"
                                control={<Radio />}
                                label={
                                    <div>
                                        <div>
                                            {t('resend_dialog.mode_last_label')}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            {t('resend_dialog.mode_last_desc')}
                                        </div>
                                    </div>
                                }
                            />
                            <FormControlLabel
                                value="custom"
                                control={<Radio />}
                                label={
                                    <div>
                                        <div>
                                            {t(
                                                'resend_dialog.mode_custom_label'
                                            )}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            {t(
                                                'resend_dialog.mode_custom_desc'
                                            )}
                                        </div>
                                    </div>
                                }
                            />
                        </RadioGroup>
                    </FormControl>

                    {mode === 'custom' && (
                        <div>
                            <FormItem
                                control={control}
                                name="date_range"
                                type="daterangepicker"
                                label={t(
                                    'report_waf.resend_dialog.date_range_label'
                                )}
                                placeholder={t(
                                    'report_waf.resend_dialog.date_range_placeholder'
                                )}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                {t('resend_dialog.retention_hint', {
                                    days: ELK_RETENTION_DAYS,
                                    min: minGte.format('DD MMM YYYY'),
                                    max: maxLte.format('DD MMM YYYY'),
                                    defaultValue: `Report retention window: ${minGte.format(
                                        'DD MMM YYYY'
                                    )} — ${maxLte.format('DD MMM YYYY')} (last ${ELK_RETENTION_DAYS} days).`,
                                })}
                            </p>
                        </div>
                    )}

                    <div className="flex justify-end gap-3 pt-4 border-t">
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            type="button"
                            disabled={isLoading}
                        >
                            {t('cancel')}
                        </Button>

                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            disabled={isLoading}
                        >
                            {isLoading
                                ? t('resend_dialog.processing')
                                : t('confirm')}
                        </Button>
                    </div>
                </div>
            </form>
        </Dialog>
    )
}

export default ResendReportDialog
